import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Award, Facebook, Instagram, Linkedin, Globe, Youtube } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SmartImage from '../components/SmartImage';
import ShareButton from '../components/ShareButton';
import api, { mediaUrl } from '../lib/api';

const FacilitatorDetail = () => {
  const { id } = useParams();
  const [f, setF] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/facilitators/${id}`).then((res) => setF(res.data)).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="App"><Header /><div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-mahogany" /></div><Footer /></div>;
  if (notFound || !f) return (
    <div className="App"><Header /><div className="text-center py-32 bg-cream"><p className="text-mahogany/70">Facilitator not found.</p><Link to="/facilitator-and-teachers" className="pill-btn mt-4 inline-flex">Back to Facilitators &amp; Teachers</Link></div><Footer /></div>
  );

  const s = f.social_links || {};
  return (
    <div className="App" data-testid="page-facilitator-detail">
      <Header />
      <main className="bg-cream">
        <section className="max-w-[1000px] mx-auto px-6 lg:px-10 py-12">
          <Link to="/facilitator-and-teachers" className="inline-flex items-center gap-2 text-mahogany/70 hover:text-mahogany mb-6" data-testid="facilitator-back"><ArrowLeft className="w-4 h-4" /> All Facilitators &amp; Teachers</Link>
          <div className="grid md:grid-cols-[360px_1fr] gap-10 items-start">
            <SmartImage src={mediaUrl(f.photo_url)} alt={f.display_name} className="rounded-3xl aspect-[3/4]" />
            <div>
              <h1 className="font-serif-display text-4xl md:text-5xl text-burgundy" data-testid="facilitator-name">{f.display_name}</h1>
              {f.title && <div className="mt-2 text-mahogany/70 uppercase tracking-wider text-sm">{f.title}</div>}
              <div className="mt-4 flex flex-wrap gap-3 text-sm text-burgundy/80">
                {f.years_experience ? <span className="inline-flex items-center gap-1.5"><Award className="w-4 h-4 text-rose-italic" /> {f.years_experience}+ years experience</span> : null}
                {(f.languages_taught || []).length > 0 && <span className="rounded-full bg-rose-soft text-mahogany px-3 py-0.5 text-xs">{f.languages_taught.join(' · ')}</span>}
              </div>
              {f.bio && <p className="mt-6 text-burgundy/85 leading-relaxed whitespace-pre-line">{f.bio}</p>}
              {(f.specializations || []).length > 0 && (
                <div className="mt-5 flex flex-wrap gap-2">{f.specializations.map((sp) => <span key={sp} className="text-sm px-3 py-1 rounded-full bg-rose-soft text-mahogany">{sp}</span>)}</div>
              )}
              <div className="mt-6 flex gap-3 items-center">
                {s.facebook && <a href={s.facebook} target="_blank" rel="noreferrer" className="text-mahogany/70 hover:text-mahogany"><Facebook className="w-5 h-5" /></a>}
                {s.instagram && <a href={s.instagram} target="_blank" rel="noreferrer" className="text-mahogany/70 hover:text-mahogany"><Instagram className="w-5 h-5" /></a>}
                {s.linkedin && <a href={s.linkedin} target="_blank" rel="noreferrer" className="text-mahogany/70 hover:text-mahogany"><Linkedin className="w-5 h-5" /></a>}
                {s.youtube && <a href={s.youtube} target="_blank" rel="noreferrer" className="text-mahogany/70 hover:text-mahogany"><Youtube className="w-5 h-5" /></a>}
                {s.website && <a href={s.website} target="_blank" rel="noreferrer" className="text-mahogany/70 hover:text-mahogany"><Globe className="w-5 h-5" /></a>}
                <ShareButton title={f.display_name} text={`Meet ${f.display_name}${f.title ? ', ' + f.title : ''} at Osho Universal`} testid="share-facilitator" className="ml-auto" />
              </div>
            </div>
          </div>

          {(f.gallery || []).length > 0 && (
            <div className="mt-12">
              <h2 className="font-serif-display text-2xl text-burgundy mb-4">Gallery</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {f.gallery.map((g) => <SmartImage key={g} src={mediaUrl(g)} alt="" className="rounded-2xl aspect-square" />)}
              </div>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default FacilitatorDetail;
